import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  CREATE_DEFAULTS,
  defaultsFrom,
  promotionFormInput,
  type PromotionDetail,
  type PromotionFormInput,
} from '../api/schemas.js';
import { useCreatePromotion, useUpdatePromotion } from '../api/mutations.js';
import { usePromotion } from '../api/queries.js';
import { PromotionFields } from '../forms/promotion-fields.js';

export function PromotionFormPage(): React.JSX.Element {
  const promotionId = useParams().promotionId ?? '';
  const isEdit = promotionId !== '';

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.PROMOTION_UPDATE : PERMISSIONS.PROMOTION_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditPromotion promotionId={promotionId} /> : <CreatePromotion />}
    </PermissionGuard>
  );
}

function CreatePromotion(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreatePromotion();

  const form = useAppForm<PromotionFormInput>({
    schema: promotionFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Promotion created',
    onSubmit: async (values) => {
      await create.mutateAsync(values);
      void navigate('/promotions');
    },
  });

  return (
    <FormPage
      title="New promotion"
      description="Create a promotion and the scopes, exclusions and stacking it applies."
      backTo="/promotions"
      backLabel="Promotions"
      submitLabel="Create promotion"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PromotionFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
      />
    </FormPage>
  );
}

function EditPromotion({ promotionId }: { readonly promotionId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdatePromotion();
  const query = usePromotion(promotionId);

  if (query.isPending) return <LoadingState label="Loading promotion" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditPromotionForm
      promotion={query.data}
      onSaved={() => {
        void navigate('/promotions');
      }}
      save={async (values) => {
        await update.mutateAsync({ promotionId, values });
      }}
    />
  );
}

function EditPromotionForm({
  promotion,
  onSaved,
  save,
}: {
  readonly promotion: PromotionDetail;
  readonly onSaved: () => void;
  readonly save: (values: PromotionFormInput) => Promise<void>;
}): React.JSX.Element {
  const form = useAppForm<PromotionFormInput>({
    schema: promotionFormInput,
    defaultValues: defaultsFrom(promotion),
    successMessage: 'Promotion updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title="Edit promotion"
      description="Change the promotion's discount, windows and scope."
      backTo="/promotions"
      backLabel="Promotions"
      submitLabel="Save promotion"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PromotionFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
      />
    </FormPage>
  );
}
